/**
 * @file timeService.ts
 * @brief Time tracking API service. Uses backend /task_management/time-logs; returns unwrapped payloads.
 */

import dayjs from 'dayjs';
import apiClient, { unwrapResponse } from './apiClient';
import { API_ENDPOINTS } from './endpoints';
import type { TimeLog, ActiveTimer, DailyTimeEntry } from '@/types';

function mapRawToTimeLog(raw: Record<string, unknown>, taskId?: string): TimeLog {
  return {
    id: String(raw.id ?? ''),
    taskId: String(raw.taskId ?? taskId ?? ''),
    userId: String(raw.userId ?? ''),
    startTime: String(raw.startTime ?? ''),
    endTime: raw.endTime != null ? String(raw.endTime) : undefined,
    duration: Number(raw.duration ?? 0),
    isManual: Boolean(raw.isManual),
    manualReason: raw.manualReason != null ? String(raw.manualReason) : undefined,
    notes: raw.notes != null ? String(raw.notes) : undefined,
    createdAt: String(raw.createdAt ?? ''),
    updatedAt: raw.updatedAt != null ? String(raw.updatedAt) : undefined,
    createdBy: raw.createdBy != null ? String(raw.createdBy) : undefined,
  };
}

function mapRawToActiveTimer(raw: Record<string, unknown>): ActiveTimer {
  const status = (raw.status as ActiveTimer['status']) ?? 'running';
  let accumulatedTime = Number(raw.accumulatedTime ?? 0);
  const startTime = String(raw.startTime ?? '');
  if (status === 'running' && startTime) {
    const startMs = new Date(startTime).getTime();
    if (!Number.isNaN(startMs)) {
      accumulatedTime += Math.floor((Date.now() - startMs) / 1000);
    }
  }
  return {
    taskId: String(raw.taskId ?? ''),
    taskTitle: String(raw.taskTitle ?? ''),
    userId: String(raw.userId ?? ''),
    startTime,
    status,
    accumulatedTime,
    pausedAt: raw.pausedAt != null ? String(raw.pausedAt) : undefined,
  };
}

export const timeService = {
  async startTimer(taskId: string): Promise<ActiveTimer> {
    const response = await apiClient.post(API_ENDPOINTS.TIME.START, { taskId });
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToActiveTimer(data);
  },

  async stopTimer(taskId: string, reason?: string): Promise<TimeLog> {
    const response = await apiClient.post(API_ENDPOINTS.TIME.STOP, { taskId, reason });
    const data = unwrapResponse<{ timeLogId?: string; duration?: number }>(response);
    return {
      id: String(data?.timeLogId ?? ''),
      taskId,
      userId: '',
      startTime: dayjs().subtract(data?.duration ?? 0, 'minute').toISOString(),
      endTime: dayjs().toISOString(),
      duration: Number(data?.duration ?? 0),
      isManual: false,
      notes: reason ?? undefined,
      createdAt: dayjs().toISOString(),
      updatedAt: dayjs().toISOString(),
    };
  },

  async pauseTimer(taskId: string): Promise<ActiveTimer> {
    const response = await apiClient.post(API_ENDPOINTS.TIME.PAUSE, { taskId });
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToActiveTimer(data);
  },

  async resumeTimer(taskId: string): Promise<ActiveTimer> {
    const response = await apiClient.post(API_ENDPOINTS.TIME.RESUME, { taskId });
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToActiveTimer(data);
  },

  async getActiveTimer(): Promise<ActiveTimer | null> {
    const response = await apiClient.get(API_ENDPOINTS.TIME.ACTIVE);
    const data = unwrapResponse<Record<string, unknown>[] | Record<string, unknown> | null>(response);
    if (!data) return null;
    // Handle both array (new) and single object (legacy)
    const timerData = Array.isArray(data) ? data[0] : data;
    if (!timerData || typeof timerData !== 'object') return null;
    const status = (timerData.status as string) ?? '';
    const taskId = timerData.taskId != null ? String(timerData.taskId) : '';
    if ((status !== 'running' && status !== 'paused') || !taskId) return null;
    return mapRawToActiveTimer(timerData);
  },

  async getAllActiveTimers(): Promise<ActiveTimer[]> {
    const response = await apiClient.get(API_ENDPOINTS.TIME.ACTIVE);
    const data = unwrapResponse<Record<string, unknown>[] | null>(response);
    if (!Array.isArray(data) || data.length === 0) return [];
    return data.map(mapRawToActiveTimer).filter(t => t.taskId && (t.status === 'running' || t.status === 'paused'));
  },

  async getTimeLogs(params?: { startDate?: string; endDate?: string; taskId?: string; userId?: string }): Promise<TimeLog[]> {
    const search = new URLSearchParams();
    search.set('pageSize', '100');
    if (params?.taskId) search.set('taskId', params.taskId);
    if (params?.userId) search.set('userId', params.userId);
    const url = `${API_ENDPOINTS.TIME.LOGS}?${search.toString()}`;
    const response = await apiClient.get(url);
    const data = unwrapResponse<{ items?: Record<string, unknown>[] }>(response);
    const items = Array.isArray(data?.items) ? data.items : [];
    return (items as Record<string, unknown>[]).map((r) => mapRawToTimeLog(r));
  },

  async getTaskTimeLogs(taskId: string): Promise<TimeLog[]> {
    const response = await apiClient.get(API_ENDPOINTS.TIME.TASK_LOGS(taskId));
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map((r) => mapRawToTimeLog(r, taskId)) : [];
  },

  async getUserTimeLogs(userId: string, startDate?: string, endDate?: string): Promise<TimeLog[]> {
    const response = await apiClient.get(API_ENDPOINTS.TIME.USER_LOGS(userId));
    const data = unwrapResponse<Record<string, unknown>[]>(response);
    return Array.isArray(data) ? data.map((r) => mapRawToTimeLog(r)) : [];
  },

  async addManualEntry(taskId: string, duration: number, notes?: string): Promise<TimeLog> {
    const endTime = dayjs();
    const startTime = endTime.subtract(duration, 'minute');
    const body = {
      taskId,
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      duration,
      notes: notes ?? undefined,
    };
    const response = await apiClient.post(API_ENDPOINTS.TIME.MANUAL_ENTRY, body);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToTimeLog(data, taskId);
  },

  async correctTimeLog(
    logId: string,
    data: { duration: number; reason: string }
  ): Promise<TimeLog> {
    const body = { duration: data.duration, notes: data.reason };
    const response = await apiClient.put(API_ENDPOINTS.TIME.CORRECT(logId), body);
    const raw = unwrapResponse<Record<string, unknown>>(response);
    return mapRawToTimeLog(raw);
  },

  async getDailySummary(date?: string): Promise<DailyTimeEntry> {
    const d = date ?? dayjs().format('YYYY-MM-DD');
    const response = await apiClient.get(`${API_ENDPOINTS.TIME.DAILY_SUMMARY}?date=${d}`);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return {
      date: String(data?.date ?? d),
      userId: String(data?.userId ?? ''),
      totalLoggedMinutes: Number(data?.totalLoggedMinutes ?? data?.totalMinutes ?? 0),
      taskBreakdown: Array.isArray(data?.taskBreakdown) ? (data.taskBreakdown as DailyTimeEntry['taskBreakdown']) : [],
    };
  },

  async getWeeklySummary(weekStart?: string): Promise<DailyTimeEntry[]> {
    const start = weekStart ?? dayjs().startOf('week').format('YYYY-MM-DD');
    const end = dayjs(start).add(6, 'day').format('YYYY-MM-DD');
    const response = await apiClient.get(`${API_ENDPOINTS.TIME.WEEKLY_SUMMARY}?start=${start}&end=${end}`);
    const data = unwrapResponse<{ byDay?: { date: string; totalMinutes: number }[] }>(response);
    const byDay = Array.isArray(data?.byDay) ? data.byDay : [];
    return byDay.map((d) => ({
      date: d.date,
      userId: '',
      totalLoggedMinutes: d.totalMinutes ?? 0,
      taskBreakdown: [],
    })) as DailyTimeEntry[];
  },

  /**
   * Get time tracking summary for a specific user
   * Returns weekly, monthly, and breakdown data
   */
  async getUserTimeSummary(userId: string): Promise<{
    thisWeek: { logged: number; expected: number };
    lastWeek: { logged: number; expected: number };
    thisMonth: { logged: number; expected: number };
    breakdown: Array<{ task: string; taskId: string; hours: number; percent: number }>;
  }> {
    const now = dayjs();
    const thisWeekStart = now.startOf('week').format('YYYY-MM-DD');
    const thisWeekEnd = now.endOf('week').format('YYYY-MM-DD');
    const lastWeekStart = now.subtract(1, 'week').startOf('week').format('YYYY-MM-DD');
    const lastWeekEnd = now.subtract(1, 'week').endOf('week').format('YYYY-MM-DD');
    const thisMonthStart = now.startOf('month').format('YYYY-MM-DD');
    const thisMonthEnd = now.endOf('month').format('YYYY-MM-DD');

    // Fetch time logs for all periods
    const response = await apiClient.get(API_ENDPOINTS.TIME.LOGS, {
      params: { userId, pageSize: 1000 },
    });
    const data = unwrapResponse<{ items?: Record<string, unknown>[] }>(response);
    const items = Array.isArray(data?.items) ? data.items : [];
    const logs = items.map((r) => mapRawToTimeLog(r));

    // Calculate this week
    const thisWeekLogs = logs.filter((log) => {
      const logDate = dayjs(log.startTime);
      return logDate.isAfter(thisWeekStart) || logDate.isSame(thisWeekStart, 'day');
    });
    const thisWeekMinutes = thisWeekLogs.reduce((sum, log) => sum + (log.duration || 0), 0);

    // Calculate last week
    const lastWeekLogs = logs.filter((log) => {
      const logDate = dayjs(log.startTime);
      return logDate.isBetween(lastWeekStart, lastWeekEnd, 'day', '[]');
    });
    const lastWeekMinutes = lastWeekLogs.reduce((sum, log) => sum + (log.duration || 0), 0);

    // Calculate this month
    const thisMonthLogs = logs.filter((log) => {
      const logDate = dayjs(log.startTime);
      return logDate.isBetween(thisMonthStart, thisMonthEnd, 'day', '[]');
    });
    const thisMonthMinutes = thisMonthLogs.reduce((sum, log) => sum + (log.duration || 0), 0);

    // Calculate breakdown by task for this week
    const taskMap = new Map<string, { taskId: string; minutes: number; task: string }>();
    thisWeekLogs.forEach((log) => {
      const taskId = log.taskId || 'unknown';
      const existing = taskMap.get(taskId) || { taskId, minutes: 0, task: `Task ${taskId.slice(0, 8)}` };
      taskMap.set(taskId, {
        ...existing,
        minutes: existing.minutes + (log.duration || 0),
      });
    });

    const breakdown = Array.from(taskMap.values())
      .sort((a, b) => b.minutes - a.minutes)
      .slice(0, 5)
      .map((item) => ({
        task: item.task,
        taskId: item.taskId,
        hours: Math.round((item.minutes / 60) * 10) / 10,
        percent: thisWeekMinutes > 0 ? Math.round((item.minutes / thisWeekMinutes) * 100) : 0,
      }));

    return {
      thisWeek: { 
        logged: Math.round((thisWeekMinutes / 60) * 10) / 10, 
        expected: 40 
      },
      lastWeek: { 
        logged: Math.round((lastWeekMinutes / 60) * 10) / 10, 
        expected: 40 
      },
      thisMonth: { 
        logged: Math.round((thisMonthMinutes / 60) * 10) / 10, 
        expected: 160 
      },
      breakdown,
    };
  },
};

export default timeService;
